import { StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { createHttpTransport } from "@tdelivr/api-client";

import { arrivalFor, LeavingForTheProvider } from "./auth/arrival.tsx";
import { ConsoleAuthority, type SignInFailure } from "./auth/authority.ts";
import { isConfigured, readOidcConfiguration } from "./auth/configuration.ts";
import { useAuthentication } from "./auth/use-authentication.ts";
import { developmentSession } from "./development-session.ts";
import { pageIdIn, urlForPage } from "./page-location.ts";
import { supportPages } from "./navigation.ts";
import type { Session } from "./session.ts";
import { CaseQueue } from "./ui/CaseQueue.tsx";
import { CaseTimeline } from "./ui/CaseTimeline.tsx";
import { CustomerLookup } from "./ui/CustomerLookup.tsx";
import { GoodwillRefunds } from "./ui/GoodwillRefunds.tsx";
import { PageOutlook } from "./ui/PageOutlook.tsx";
import { RepliesAndMacros } from "./ui/RepliesAndMacros.tsx";
import { Satisfaction } from "./ui/Satisfaction.tsx";
import { SessionEnded } from "./ui/SessionEnded.tsx";
import { Shell } from "./ui/Shell.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { SignOutDialog } from "./ui/SignOutDialog.tsx";
import { StateView } from "./ui/StateView.tsx";
import "./styles.css";

/**
 * The console, running.
 *
 * Two ways in, and only one of them exists in a production build.
 *
 * **Sign in.** OpenID Connect, authorization code flow with PKCE, against the provider named by
 * `VITE_OIDC_ISSUER`. The shape is not a choice: a browser application cannot hold a client secret,
 * so a public client with PKCE is the only correct option. `src/auth/` holds the whole of it and
 * `docs/adr/0018-the-console-signs-in-with-pkce.md` records why it is written out rather than
 * imported.
 *
 * An unauthenticated arrival does not draw a screen first. It goes straight to the provider's own
 * page, which is the sign-in page —
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md`, superseding ADR 0018 on that one point
 * and nothing else. `auth/arrival.tsx` holds the decision, including the two unauthenticated
 * arrivals that must be shown a page rather than redirected.
 *
 * **Development claims.** `VITE_ACCESS_TOKEN_CLAIMS`, read by `development-session.ts`, which is the
 * path the fidelity harness and the tests run on. Compiled out of a production build rather than
 * merely discouraged in it — see that file for the two mechanisms and the README for the measurement.
 *
 * Four of the design's six pages are routed. The other two fall through to `PageOutlook`, which
 * says what each is waiting for — and on this console both are waiting on a service that does not
 * exist rather than on a route that is not published: `Replies and macros` cites SUP-04, which is
 * the customer-360 read model and holds no macro, and `Satisfaction` cites SUP-05, which is the
 * escalation service and holds no rating. `navigation.ts` carries both measured reasons.
 *
 * Four rather than six is the measurement and not a decision about effort, and three decisions
 * taken elsewhere are what moved it from nought: **ADR 0078** published the support-case listing
 * and the timeline read, whose absence
 * `docs/design/CONSOLE_SCREEN_SUPPORT.md` recorded as "every support-case operation is
 * `/internal/v1`"; **ADR 0027** published `GET /v1/refunds`, the first of four absences on
 * `Goodwill and refunds`; and `GET /v1/customer-profiles` answers a collection, which is what
 * `Customer lookup` needs to resolve anything at all. `tooling/scripts/report-completion.mjs`
 * counts a screen as built exactly when this file routes it, which is the same condition as an
 * agent being able to open it.
 */

/**
 * Tenant, then the console, then the page's menu group, then the page.
 *
 * All four of the design's crumbs, and the middle one is the thing most easily got wrong. This design
 * file's own trail is `EAM › Support › Work › Case queue`: the second crumb is **the console**, not
 * the page's group. The dispatch console drew `EAM › In flight › …` for weeks, where its design
 * draws `EAM › Operations › …` — a menu group where the console's name belongs — so the trail named
 * the page twice and never named where the agent was.
 *
 * The tenant is first for the reason the chrome states it at all: an actor who can cross tenants must
 * always know which side of the boundary they are on. The third crumb is the design's own `NAV`
 * group, so the trail reads the way the rail does.
 *
 * One departure, and it is the same one R-07 and R-11 measured: the design's first crumb is `EAM`, a
 * short tenant name, and **nothing on this platform publishes one**. The full name goes there
 * instead, which is longer than the design's and is the only thing that is true.
 */
function crumbsFor(tenantName: string, pageId: string): readonly string[] {
  const page = supportPages.find((candidate) => candidate.id === pageId);
  return [tenantName, "Support", page?.section ?? "Work", page?.title ?? "Unknown page"];
}

interface ConsoleProps {
  readonly session: Session;
  /**
   * The bearer token for the next request, or undefined when there is no token to send.
   *
   * A function rather than a string, and asynchronous, because that is how a token stays fresh: the
   * authority refreshes one that is about to expire before handing it over, so a request never goes
   * out carrying a token a service will refuse. `createHttpTransport` already accepted a promise
   * here, so nothing in `packages/api-client` changed.
   *
   * Absent on the development path, which has no token at all. That is what the harness runs with
   * and what it has always run with: the stubs answer without looking at an authorization header.
   */
  readonly accessToken?: () => Promise<string | undefined>;
  readonly onSignOut?: () => void;
  /** Rendered in place of the page, with the chrome intact, as the design draws it. */
  readonly expired?: ReactNode;
}

function Console({ session, accessToken, onSignOut, expired }: ConsoleProps): ReactNode {
  /*
   * The open page, in the address bar.
   *
   * It was React state alone, which meant every load opened the case queue and there was no way to say
   * which page an agent had asked for — including to the sign-in flow, which has to send them back
   * to it. `page-location.ts` is the whole of the mechanism.
   */
  const [pageId, setPageId] = useState<string>(() => pageIdIn(window.location.href));
  /**
   * The case the timeline page is a view of.
   *
   * Held here rather than on either screen, because it belongs to neither: the queue chooses it and
   * the timeline reads it, and the design's timeline heading — "Everything that happened on case
   * cs_4471" — is what makes it a selection rather than a filter.
   *
   * **Not in the address bar**, which is a departure from how the open page is held and is
   * deliberate. `page-location.ts` exists so that a page survives leaving for the identity
   * provider and coming back, and so that an address can be shared; a case identifier in a URL
   * shared into a chat is a tenant's customer's case identifier in a chat. The page is addressable
   * and the selection is not.
   */
  const [selectedCaseId, setSelectedCaseId] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Back and forward move between pages rather than out of the console, now that pages are
    // addressable. Without this the browser's back button leaves a single-page application that has
    // pushed history entries, which is the worst of both.
    const onPopState = (): void => {
      setPageId(pageIdIn(window.location.href));
    };
    window.addEventListener("popstate", onPopState);
    return (): void => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const navigate = useCallback((next: string) => {
    window.history.pushState(null, "", urlForPage(window.location.href, next));
    setPageId(next);
  }, []);

  /*
   * One transport for the session, and memoised.
   *
   * The token and the tenant are read per request rather than captured, so a refreshed token is
   * picked up without rebuilding anything the screens hold. Memoised because the header's inbox
   * effect depends on the transport's identity, and the authentication state above this component
   * now changes — a fresh object on every render would reload the inbox on every render.
   */
  const transport = useMemo(
    () =>
      createHttpTransport({
        // The page's own origin, not "/". The transport resolves each path with `new URL(path, base)`,
        // and `new URL("v1/…", "/")` throws "Invalid base URL" because a bare slash is not an absolute
        // URL — so the default configuration could not make a single request, and every screen rendered
        // its error state. Found the first time the console was rendered by the fidelity check rather
        // than reasoned about, which is the argument for rendering it.
        //
        // Same origin is also the right default once the gateway exists: Document 17 WEB-0743 forbids
        // exposing the services directly, so the console and its API are one origin in every environment
        // that is not a developer's machine.
        baseUrl: import.meta.env.VITE_PLATFORM_URL ?? window.location.origin,
        tenantId: () => session.tenantId,
        ...(accessToken === undefined ? {} : { authorization: accessToken }),
      }),
    [session.tenantId, accessToken],
  );

  return (
    <Shell
      session={session}
      currentPageId={pageId}
      onNavigate={navigate}
      transport={transport}
      {...(onSignOut === undefined ? {} : { onSignOut })}
    >
      {expired !== undefined ? (
        expired
      ) : pageId === "cases" ? (
        <CaseQueue
          transport={transport}
          tenantId={session.tenantId}
          scopes={session.scopes}
          crumbs={crumbsFor(session.tenantName, pageId)}
          onSelectCase={setSelectedCaseId}
        />
      ) : pageId === "lookup" ? (
        <CustomerLookup
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "timeline" ? (
        <CaseTimeline
          transport={transport}
          tenantId={session.tenantId}
          caseId={selectedCaseId}
          crumbs={crumbsFor(session.tenantName, pageId)}
          onOpenQueue={() => navigate("cases")}
        />
      ) : pageId === "refunds" ? (
        <GoodwillRefunds transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "macros" ? (
        /*
         * The fifth, and the one whose blocked reason was wrong about three of the four things
         * it said were missing: NTF-07 has held a saved reply with variables in a locale since
         * it was written. ADR 0088.
         */
        <RepliesAndMacros transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "csat" ? (
        /*
         * The sixth, which completes this console — and the one whose blocked reason turned out to
         * be **right**. It said nothing models support satisfaction and cited SUP-05, the
         * escalation service. ADR 0088 had just shown that a correct citation can sit beside three
         * wrong claims, so the question "does anything else hold it" was asked first: RAT-01 is the
         * only rating service, its `transactionType` is a one-member enum and it publishes no
         * listing at all. So the record genuinely did not exist, and ADR 0090 adds it to SUP-01 —
         * the service that owns the case, where "after a case closes" is already a row.
         */
        <Satisfaction
          transport={transport}
          session={session}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : (
        <PageOutlook session={session} />
      )}
    </Shell>
  );
}

/**
 * Everything above the console: leaving to sign in, the code exchange, the expiry and the sign-out.
 *
 * One switch over one state, which is the point of `ConsoleAuthority` holding all of it. The question
 * "is this agent signed in" has exactly one answer at any moment, and the chrome, the transport and
 * this switch all read it from the same place.
 *
 * Exactly one of its branches leaves this page: `signed_out`, and only when `arrivalFor` says the
 * arrival has nothing to report. Every other branch draws something. That asymmetry is the whole of
 * ADR 0022's caution — a state that is unauthenticated *because something went wrong* must be read,
 * not retried, or the console redirects into the same failure for as long as the browser lets it.
 */
function Authenticated({ authority }: { readonly authority: ConsoleAuthority }): ReactNode {
  const state = useAuthentication(authority);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  /**
   * Whether the agent has dismissed the sign-in screen's failure.
   *
   * The design's "Use a different account" returns to the start step. There is nothing to ask the
   * authority for — the failure is already final — so it is this component's state rather than the
   * authority's, which keeps the authority a statement about the session rather than about a screen.
   *
   * It returns to a page rather than to the provider on purpose. The agent has just been told why
   * a sign-in failed; the next move is theirs.
   */
  const [dismissedFailure, setDismissedFailure] = useState(false);

  const providerName = authority.providerName;
  const accessToken = useCallback(() => authority.accessToken(), [authority]);
  /*
   * Leave for the provider, from a button an agent pressed.
   *
   * No argument any more. It carried the email from the design's first step as `login_hint`, and ADR
   * 0022 removed that step: the provider's own form asks for the address, so collecting it here only
   * to send it there was the hop the agent decided against.
   */
  const signIn = useCallback(() => {
    setDismissedFailure(false);
    void authority.beginSignIn();
  }, [authority]);

  switch (state.kind) {
    case "starting":
      // The whole page, before anything has been decided. The design's loading state, which this
      // console renders in one place for every screen, centred because here it is the page.
      return (
        <div className="boot">
          <StateView state={{ kind: "loading" }} purpose="The console is starting." />
        </div>
      );

    case "signed_out":
      /*
       * The one render site that became a redirect, and the only one.
       *
       * Nobody is signed in and nothing has gone wrong, so there is nothing to say that the
       * provider's own page does not say better. `arrivalFor` decides, rather than this component
       * redirecting on sight, because two unauthenticated arrivals must still be given a page: the
       * agent who has just signed out, who would otherwise be sent back to the form they left,
       * and the tab that has already been to the provider and returned with no answer, which would
       * otherwise bounce between the two for ever. Both then land on the step below, which is the
       * design's first step with its email field removed.
       */
      return arrivalFor(state).kind === "to_the_provider" ? (
        <LeavingForTheProvider authority={authority} />
      ) : (
        <SignIn
          step="start"
          afterSignOut={state.afterSignOut}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "authenticating":
      // Left as a page, deliberately. This is the redirect in flight — discovery running, or the
      // code coming back to be exchanged — and it is what the agent sees for the moment before
      // the browser leaves. A redirect here would be a redirect during a redirect.
      return (
        <SignIn
          step="authenticating"
          afterSignOut={false}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "failed":
      /*
       * Left as a page, and this is the one that matters most.
       *
       * A sign-in that did not finish is exactly the state in which redirecting would loop: the
       * console would leave for the provider, fail the same way, and leave again, so an agent
       * whose account is not linked would never get to read the sentence telling them so. The
       * failure is drawn and the only way back out to the provider is a button they press.
       */
      return (
        <SignIn
          step={dismissedFailure ? "start" : "failed"}
          afterSignOut={false}
          providerName={providerName}
          failure={state.failure}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "expired":
      return (
        <Console
          session={state.session}
          accessToken={accessToken}
          expired={
            <SessionEnded
              tenantName={state.session.tenantName}
              endedAt={state.endedAt}
              lastedMs={state.lastedMs}
              onSignInAgain={() => {
                void authority.beginSignIn();
              }}
            />
          }
        />
      );

    case "signed_in":
      return (
        <>
          <Console
            session={state.session}
            accessToken={accessToken}
            onSignOut={() => setConfirmingSignOut(true)}
          />
          {confirmingSignOut ? (
            <SignOutDialog
              onDismiss={() => setConfirmingSignOut(false)}
              onConfirm={() => {
                setConfirmingSignOut(false);
                void authority.signOut();
              }}
            />
          ) : null}
        </>
      );
  }
}

/**
 * A build with no identity provider configured, said in the design's failure card.
 *
 * Also left as a page. There is no authorization endpoint to redirect to — that is what the card
 * says — so this is the state where a redirect is not merely a loop but impossible.
 */
function unconfigured(missing: readonly string[]): SignInFailure {
  return {
    code: "console_not_configured",
    headline: "This console has no identity provider configured",
    detail: `It was built without ${missing.join(" and ")}, so there is nowhere to send you to sign in. A platform administrator sets those when the console is built or served. Retrying won't change this.`,
    retryable: false,
  };
}

const root = document.getElementById("root");
if (root === null) throw new Error("The console has no mount point");

/*
 * Rendered synchronously, in every case.
 *
 * Not an aesthetic preference. Module scripts run before `DOMContentLoaded`, and the fidelity
 * harness asks whether a navigation button `isVisible()` immediately after that event without
 * waiting for it — so a bootstrap that awaited anything before its first render would have the
 * harness conclude the rail was absent and start opening a drawer that is not there at 1440. The
 * asynchronous part of signing in happens after the first paint, behind the design's loading state.
 */
const development = import.meta.env.DEV ? developmentSession() : undefined;

if (development !== undefined) {
  createRoot(root).render(
    <StrictMode>
      <Console session={development} />
    </StrictMode>,
  );
} else {
  const configuration = readOidcConfiguration(import.meta.env, window.location.origin);
  if (!isConfigured(configuration)) {
    createRoot(root).render(
      <StrictMode>
        <SignIn
          step="failed"
          afterSignOut={false}
          providerName="your organisation's sign-in"
          failure={unconfigured(configuration.missing)}
          onContinue={() => {}}
          onStartAgain={() => {}}
        />
      </StrictMode>,
    );
  } else {
    const authority = new ConsoleAuthority({
      configuration,
      fetch: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      // Per tab. Two tabs signing in at once must not read each other's code verifier, and a
      // verifier left in `localStorage` would outlive the browser session it belongs to.
      storage: window.sessionStorage,
      currentUrl: (): string => window.location.href,
      replaceUrl: (url: string): void => {
        window.history.replaceState(null, "", url);
      },
      navigate: (url: string): void => {
        window.location.assign(url);
      },
      now: (): number => Date.now(),
    });

    createRoot(root).render(
      <StrictMode>
        <Authenticated authority={authority} />
      </StrictMode>,
    );
    // After the first render, so the loading state is on screen while discovery and the code
    // exchange happen.
    void authority.start();
  }
}
